import { html, type TemplateResult } from 'lit-html';
import { api, ApiError, get } from '../api.js';
import { act, app, confirmDialog, copy, emptyRow, isAdmin, toast } from '../app.js';
import { connectorHref, type ConnectorUi } from '../connector-ui.js';
import { mailPill, pill, when } from '../format.js';
import type { Connector, FieldDef, FieldType, Mailpiece, Template } from '../types.js';

const BASE = '/connectors/stannp';
/** #/connectors/stannp/{tab}/{rest} */
const href = (tab: string, rest = '') => connectorHref('stannp', tab, rest);

// ---------------------------------------------------------------------------------------------
// Settings tab: account panels
// ---------------------------------------------------------------------------------------------
interface Account {
  balance: unknown;
  balance_error: string | null;
  send_url: string;
  webhook_url: string;
  test_mode: { environment: boolean; setting: boolean };
}
let account: Account | null = null;
let templates: Template[] = [];

async function loadAccount(): Promise<void> {
  account = await get<Account>(`${BASE}/account`);
}

function balanceText(b: unknown): string {
  if (b && typeof b === 'object' && 'balance' in b) return `$${String((b as { balance: unknown }).balance)}`;
  return b === null || b === undefined ? '—' : String(b);
}

function accountPanels(c: Connector): TemplateResult {
  const a = account;
  return html`
    ${a?.test_mode.environment ? html`<div class="banner info mt-5"><b>Test environment:</b> every send is in Stannp test mode (PDF only, nothing posted).</div>` : ''}
    <div class="grid two">
      <div class="card"><h2>Account</h2>
        <dl class="kv">
          <dt>Balance</dt><dd>${a?.balance_error ? html`<span class="muted">${a.balance_error}</span>` : balanceText(a?.balance)}</dd>
          <dt>Send URL</dt><dd><code>${a?.send_url}</code></dd>
          <dt>Webhook URL</dt><dd><code>${a?.webhook_url}</code> <button class="btn quiet compact" @click=${() => copy(a?.webhook_url ?? '')}>Copy</button></dd>
        </dl>
        <p class="hint mt-4">In Stannp › Settings › Webhooks, add the webhook URL for <b>mailpiece_status</b> with the same signing secret as
          the <b>Webhook signing secret</b> below${c.secrets.find((s) => s.name === 'webhook_secret')?.configured ? '' : ' (not set yet)'}.</p>
      </div>
      <div class="card"><h2>How a send works</h2>
        <ol class="steps">
          <li>MoEngage posts <code>template_id</code>, <code>ref_id</code> and the <code>data</code> fields to the send URL.</li>
          <li>The data is checked against the template's fields (422 if something is missing).</li>
          <li>The letter is held for the template's hold time, so it can be cancelled on the <a href=${href('mail')}>Mail</a> tab.</li>
          <li>Then it goes to Stannp, with up to 5 attempts. Status updates arrive by webhook.</li>
        </ol>
      </div>
    </div>`;
}

// ---------------------------------------------------------------------------------------------
// Templates tab: list (#/connectors/stannp/templates)
// ---------------------------------------------------------------------------------------------
async function loadTemplateList(): Promise<void> {
  templates = (await get<{ templates: Template[] }>(`${BASE}/templates`)).templates;
}

interface CatalogEntry {
  stannp_template_id: string;
  name: string;
  type: 'letter' | 'postcard';
  size: string | null;
  pages: number | null;
  hidden: boolean;
  updated: string | null;
  merge_tags: string[];
  registered: boolean;
}
/** Import panel state: null = closed. */
let catalog: CatalogEntry[] | null = null;
let catalogError: string | null = null;
let picked = new Set<string>();
let showHidden = false;
const importOpts = { hold_hours: 24, dedupe_group: '' };

async function openImport(): Promise<void> {
  catalog = [];
  catalogError = null;
  picked = new Set();
  app.rerender();
  try {
    catalog = (await get<{ templates: CatalogEntry[] }>(`${BASE}/stannp-templates`)).templates;
  } catch (err) {
    catalog = null;
    catalogError = (err as Error).message;
  }
  app.rerender();
}

async function runImport(): Promise<void> {
  const r = await act(
    () =>
      api<{ imported: string[]; skipped: { id: string; reason: string }[] }>('POST', `${BASE}/templates/import`, {
        ids: [...picked],
        hold_hours: Number(importOpts.hold_hours),
        dedupe_group: importOpts.dedupe_group.trim() || null,
      }),
    null,
    loadTemplateList,
  );
  if (!r) return;
  toast(`Imported ${r.imported.length} template${r.imported.length === 1 ? '' : 's'}${r.skipped.length ? `, skipped ${r.skipped.length}` : ''}. Check each one's fields, then switch it on.`);
  catalog = null;
  app.rerender();
}

function importPanel(): TemplateResult {
  if (catalogError) return html`<div class="banner critical mt-4">Could not read the Stannp templates: ${catalogError} <button class="btn quiet compact" @click=${() => { catalogError = null; app.rerender(); }}>Close</button></div>`;
  if (!catalog) return html``;
  const shown = catalog.filter((e) => showHidden || !e.hidden);
  const toggle = (id: string, on: boolean) => {
    if (on) picked.add(id);
    else picked.delete(id);
    app.rerender();
  };
  return html`<section class="panel card">
    <div class="row-between"><h2>Import from Stannp</h2><button class="btn quiet compact" @click=${() => { catalog = null; app.rerender(); }}>Close</button></div>
    <p class="hint">Designs in your Stannp account. Imported templates arrive <b>switched off</b>, with the address fields and any merge tags found in the design.
      Open each one, check its fields match the design, then switch it on.</p>
    ${catalog.length === 0 ? html`<div class="loading"><span class="spinner" aria-hidden="true"></span>Asking Stannp…</div>` : html`
      <label class="check mb-4"><input type="checkbox" .checked=${showHidden} @change=${(e: Event) => { showHidden = (e.target as HTMLInputElement).checked; app.rerender(); }}> Show templates hidden in Stannp</label>
      <div class="table-wrap"><table>
        <thead><tr><th></th><th>Stannp template</th><th>Type</th><th>Merge tags found</th><th>Updated</th></tr></thead>
        <tbody>${shown.length
          ? shown.map((e) => html`<tr>
              <td>${e.registered ? '' : html`<input type="checkbox" aria-label="Import ${e.name}" .checked=${picked.has(e.stannp_template_id)} @change=${(ev: Event) => toggle(e.stannp_template_id, (ev.target as HTMLInputElement).checked)}>`}</td>
              <td><b>${e.name}</b> ${e.hidden ? pill('Hidden') : ''}<br><code class="id">${e.stannp_template_id}</code></td>
              <td>${e.type === 'letter' ? 'Letter' : 'Postcard'}${e.size ? html`<br><span class="muted">${e.size}${e.pages ? `, ${e.pages}p` : ''}</span>` : ''}</td>
              <td>${e.registered ? pill('Registered', 'positive') : e.merge_tags.length ? html`<span class="muted">${e.merge_tags.join(', ')}</span>` : html`<span class="muted">none found: add them by hand</span>`}</td>
              <td class="nowrap">${when(e.updated)}</td></tr>`)
          : emptyRow(5, 'No templates in the Stannp account.')}</tbody>
      </table></div>
      <div class="form-row mt-4">
        <label class="field">Hold before sending (hours)<input type="number" min="0" max="720" .value=${String(importOpts.hold_hours)} @input=${(e: Event) => (importOpts.hold_hours = Number((e.target as HTMLInputElement).value))}></label>
        <label class="field">Once per person group<input placeholder="blank = no limit" .value=${importOpts.dedupe_group} @input=${(e: Event) => (importOpts.dedupe_group = (e.target as HTMLInputElement).value)}></label>
      </div>
      <div class="actions-row"><button class="btn primary" ?disabled=${picked.size === 0} @click=${runImport}>Import ${picked.size || ''} selected</button></div>`}
  </section>`;
}

function templateList(): TemplateResult {
  const admin = isAdmin();
  return html`
    <section>
      <div class="row-between"><h2>Registered templates</h2>
        ${admin ? html`<span class="actions-row no-margin"><button class="btn compact" @click=${openImport}>Import from Stannp</button><a class="btn primary compact" href=${href('templates', 'new')}>Register by ID</a></span>` : ''}</div>
      <p class="hint">Only template IDs registered here can be sent. Each one lists the data fields its Stannp design merges in.</p>
      ${importPanel()}
      <div class="table-wrap ${catalog || catalogError ? 'mt-5' : ''}"><table class="clickable">
        <thead><tr><th>Template</th><th>Type</th><th>Hold</th><th>Once per person</th><th>Fields</th><th></th></tr></thead>
        <tbody>${templates.length
          ? templates.map(
              (t) => html`<tr @click=${() => (location.hash = href('templates', t.stannp_template_id))}>
                <td><b>${t.name}</b><br><code class="id">${t.stannp_template_id}</code></td>
                <td>${t.type === 'letter' ? 'Letter' : `Postcard ${t.options.size ?? ''}`}</td>
                <td>${t.hold_hours ? `${t.hold_hours}h` : 'None'}</td>
                <td>${t.dedupe_group ? html`<code>${t.dedupe_group}</code>` : html`<span class="muted">No limit</span>`}</td>
                <td>${t.fields.filter((f) => f.required).length} required, ${t.fields.length} total</td>
                <td>${t.enabled ? '' : pill('Off')}</td>
              </tr>`,
            )
          : emptyRow(6, 'No templates registered yet. Import them from Stannp, or register one by ID.')}</tbody>
      </table></div>
    </section>`;
}

// ---------------------------------------------------------------------------------------------
// Templates tab: editor (#/connectors/stannp/templates/{id} or …/templates/new)
// ---------------------------------------------------------------------------------------------
interface Draft extends Omit<Template, 'version' | 'created_at' | 'created_by' | 'updated_at' | 'updated_by'> {
  version: number;
}
let draft: Draft | null = null;
let isNew = false;
let original: Template | null = null;
let testData = '';
let testResult: { outcome: string; mailpiece: Mailpiece; ignored_fields: string[] } | null = null;
let testError: string | null = null;

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'us_state', label: 'US state' },
  { value: 'zip', label: 'ZIP code' },
  { value: 'date', label: 'Date' },
  { value: 'url', label: 'URL' },
];

const ADDRESS: FieldDef[] = [
  { key: 'firstname', label: 'First name', required: true, type: 'text' },
  { key: 'lastname', label: 'Last name', required: true, type: 'text' },
  { key: 'address1', label: 'Address line 1', required: true, type: 'text' },
  { key: 'address2', label: 'Address line 2', required: false, type: 'text' },
  { key: 'city', label: 'City', required: true, type: 'text' },
  { key: 'state', label: 'State', required: true, type: 'us_state' },
  { key: 'zipcode', label: 'ZIP code', required: true, type: 'zip' },
];

function sampleValue(f: FieldDef): string {
  const byKey: Record<string, string> = {
    firstname: 'Pat',
    lastname: 'Sample',
    address1: '100 Main St',
    address2: '',
    city: 'Tampa',
    state: 'FL',
    zipcode: '33602',
    plan: 'Sample Advantage Plan (HMO)',
    plan_type: 'MAPD',
  };
  if (f.key in byKey) return byKey[f.key]!;
  const next = new Date();
  next.setMonth(next.getMonth() + 1, 1);
  return { text: 'Sample', us_state: 'FL', zip: '33602', date: next.toISOString().slice(0, 10), url: 'https://example.com/logo.png' }[f.type];
}

const sampleData = (fields: FieldDef[]) => Object.fromEntries(fields.map((f) => [f.key, sampleValue(f)]));

function moengageBody(t: Draft): string {
  const data = Object.fromEntries(t.fields.map((f) => [f.key, `{{${f.label ?? f.key}}}`]));
  return JSON.stringify({ template_id: t.stannp_template_id || '<template id>', ref_id: '{{CRM client ID}}', data }, null, 2);
}

async function loadTemplate(param: string): Promise<void> {
  testResult = null;
  testError = null;
  isNew = param === 'new';
  if (isNew) {
    original = null;
    draft = { stannp_template_id: '', name: '', description: '', type: 'letter', enabled: true, hold_hours: 24, dedupe_group: null, options: {}, fields: ADDRESS.map((f) => ({ ...f })), version: 0 };
  } else {
    original = (await get<{ template: Template }>(`${BASE}/templates/${encodeURIComponent(param)}`)).template;
    draft = structuredClone(original);
  }
  testData = JSON.stringify(sampleData(draft.fields), null, 2);
}

function fieldRow(f: FieldDef, i: number, editable: boolean): TemplateResult {
  const set = (patch: Partial<FieldDef>) => {
    Object.assign(draft!.fields[i]!, patch);
    app.rerender();
  };
  return html`<tr>
    <td><input aria-label="Field key" .value=${f.key} ?disabled=${!editable} pattern="[a-z][a-z0-9_]{0,39}" @input=${(e: Event) => set({ key: (e.target as HTMLInputElement).value.trim() })}></td>
    <td><input aria-label="Label" .value=${f.label ?? ''} ?disabled=${!editable} @input=${(e: Event) => set({ label: (e.target as HTMLInputElement).value || undefined })}></td>
    <td><select aria-label="Type" ?disabled=${!editable} @change=${(e: Event) => set({ type: (e.target as HTMLSelectElement).value as FieldType })}>
      ${FIELD_TYPES.map((t) => html`<option value=${t.value} ?selected=${t.value === f.type}>${t.label}</option>`)}</select></td>
    <td><input type="checkbox" aria-label="Required" .checked=${f.required} ?disabled=${!editable} @change=${(e: Event) => set({ required: (e.target as HTMLInputElement).checked })}></td>
    <td><input type="number" min="1" max="2000" aria-label="Max length" .value=${f.max_length ? String(f.max_length) : ''} ?disabled=${!editable || f.type !== 'text'}
      @input=${(e: Event) => set({ max_length: Number((e.target as HTMLInputElement).value) || undefined })}></td>
    <td>${editable ? html`<button type="button" class="btn quiet compact icon-only" aria-label="Remove ${f.key}" @click=${() => {
      draft!.fields.splice(i, 1);
      app.rerender();
    }}>✕</button>` : ''}</td>
  </tr>`;
}

async function save(): Promise<void> {
  const d = draft!;
  const id = d.stannp_template_id.trim();
  if (!/^\d{1,12}$/.test(id)) return toast('The Stannp template ID is the number shown in Stannp › Templates.', 'critical');
  if (isNew && (await get<{ templates: Template[] }>(`${BASE}/templates`)).templates.some((t) => t.stannp_template_id === id)) {
    return toast(`Template ${id} is already registered.`, 'critical');
  }
  const body = {
    name: d.name,
    description: d.description,
    type: d.type,
    enabled: d.enabled,
    hold_hours: Number(d.hold_hours),
    dedupe_group: d.dedupe_group?.trim() ? d.dedupe_group.trim() : null,
    options: Object.fromEntries(Object.entries(d.options).filter(([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && !v.length))),
    fields: d.fields.map((f) => ({ ...f, ...(f.type !== 'text' ? { max_length: undefined } : {}) })),
    version: isNew ? 0 : d.version,
  };
  const r = await act(() => api<{ template: Template }>('PUT', `${BASE}/templates/${id}`, body), 'Template saved');
  if (r) {
    if (isNew) location.hash = href('templates', id);
    else {
      original = r.template;
      draft = structuredClone(r.template);
      app.rerender();
    }
  }
}

async function removeTemplate(): Promise<void> {
  const ok = await confirmDialog({
    title: `Unregister template ${draft!.stannp_template_id}?`,
    body: 'Sends with this template ID will be refused (422). Mail already held still goes out. The design in Stannp is not touched.',
    confirmLabel: 'Unregister',
    tone: 'danger',
  });
  if (ok && (await act(() => api('DELETE', `${BASE}/templates/${draft!.stannp_template_id}`), 'Template unregistered'))) location.hash = href('templates');
}

async function sendTest(): Promise<void> {
  testError = null;
  testResult = null;
  let data: unknown;
  try {
    data = JSON.parse(testData);
  } catch {
    testError = 'The sample data is not valid JSON.';
    return app.rerender();
  }
  app.rerender();
  try {
    testResult = await api('POST', `${BASE}/templates/${draft!.stannp_template_id}/test`, { data });
  } catch (err) {
    const e = err as ApiError;
    testError = e.message;
  }
  app.rerender();
}

function templateEditor(): TemplateResult {
  {
    const d = draft;
    if (!d) return html``;
    const editable = isAdmin();
    const dirty = isNew || JSON.stringify(d) !== JSON.stringify(original);
    const set = (patch: Partial<Draft>) => {
      Object.assign(d, patch);
      app.rerender();
    };
    const setOpt = (patch: Partial<Draft['options']>) => {
      d.options = { ...d.options, ...patch };
      app.rerender();
    };
    return html`
      <p class="crumb"><a href=${href('templates')}>‹ Templates</a></p>
      <h2>${isNew ? 'Register a template' : d.name}</h2>
      <p class="hint">${isNew ? 'Register a design you built in Stannp, so callers can send it by ID.' : html`Stannp template <code>${d.stannp_template_id}</code>. Last saved ${when(original?.updated_at)} by ${original?.updated_by}.`}</p>
      <form class="form-stack" @submit=${(e: Event) => { e.preventDefault(); void save(); }}>
        <div class="form-row">
          <label class="field">Stannp template ID<input required inputmode="numeric" .value=${d.stannp_template_id} ?disabled=${!isNew || !editable}
            @input=${(e: Event) => set({ stannp_template_id: (e.target as HTMLInputElement).value.trim() })}></label>
          <label class="field">Type<select ?disabled=${!editable} @change=${(e: Event) => set({ type: (e.target as HTMLSelectElement).value as Draft['type'] })}>
            <option value="letter" ?selected=${d.type === 'letter'}>Letter</option><option value="postcard" ?selected=${d.type === 'postcard'}>Postcard</option></select></label>
        </div>
        <label class="field">Name<input required maxlength="120" .value=${d.name} ?disabled=${!editable} @input=${(e: Event) => set({ name: (e.target as HTMLInputElement).value })}></label>
        <label class="field">When it is used<input maxlength="500" .value=${d.description} ?disabled=${!editable} placeholder="e.g. welcome letter for new enrollments"
          @input=${(e: Event) => set({ description: (e.target as HTMLInputElement).value })}></label>
        <div class="form-row">
          <label class="field">Hold before sending (hours)<input type="number" min="0" max="720" step="1" .value=${String(d.hold_hours)} ?disabled=${!editable}
            @input=${(e: Event) => set({ hold_hours: Number((e.target as HTMLInputElement).value) })}></label>
          <label class="field">Once per person group<input .value=${d.dedupe_group ?? ''} ?disabled=${!editable} placeholder="blank = no limit" pattern="[a-z0-9][a-z0-9\\-]{0,62}"
            @input=${(e: Event) => set({ dedupe_group: (e.target as HTMLInputElement).value || null })}></label>
          <label class="field">Size<input .value=${d.options.size ?? ''} ?disabled=${!editable} placeholder=${d.type === 'postcard' ? '4x6, 6x9 or 6x11' : 'template default'}
            @input=${(e: Event) => setOpt({ size: (e.target as HTMLInputElement).value || undefined })}></label>
        </div>
        <p class="hint">The hold is the window to cancel on the Mail tab (the CRM used 24h). Templates sharing a group mail each person (ref_id) only once between them;
          a forced resend still can.</p>
        <div class="form-row">
          <label class="field">Double-sided<select ?disabled=${!editable} @change=${(e: Event) => {
            const v = (e.target as HTMLSelectElement).value;
            setOpt({ duplex: v === '' ? undefined : v === 'true' });
          }}>
            <option value="" ?selected=${d.options.duplex === undefined}>Template default</option>
            <option value="true" ?selected=${d.options.duplex === true}>Yes</option>
            <option value="false" ?selected=${d.options.duplex === false}>No</option></select></label>
          <label class="field">Add-ons<input .value=${(d.options.addons ?? []).join(', ')} ?disabled=${!editable} placeholder="e.g. FIRST_CLASS"
            @change=${(e: Event) => setOpt({ addons: (e.target as HTMLInputElement).value.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) })}></label>
        </div>
        <label class="check"><input type="checkbox" .checked=${d.enabled} ?disabled=${!editable} @change=${(e: Event) => set({ enabled: (e.target as HTMLInputElement).checked })}>
          Accept sends for this template</label>

        <div><span class="label">Data fields</span>
          <p class="hint">Each field is sent to Stannp as <code>recipient[key]</code>, so keys must match the merge tags in the Stannp design.
            Address fields are required. Fields not listed here are dropped.</p>
          <div class="table-wrap"><table class="fields">
            <thead><tr><th>Key</th><th>Label</th><th>Type</th><th>Required</th><th>Max length</th><th></th></tr></thead>
            <tbody>${d.fields.map((f, i) => fieldRow(f, i, editable))}</tbody>
          </table></div>
          ${editable ? html`<div class="actions-row"><button type="button" class="btn compact" @click=${() => {
            d.fields.push({ key: '', required: false, type: 'text' });
            app.rerender();
          }}>Add a field</button></div>` : ''}
        </div>
        ${editable
          ? html`<div class="savebar ${dirty ? 'dirty' : ''}"><span>${dirty ? 'Unsaved changes' : 'Saved'}</span>
              ${!isNew ? html`<button type="button" class="btn destructive outline" @click=${removeTemplate}>Unregister</button>` : ''}
              <button class="btn primary" ?disabled=${!dirty}>${isNew ? 'Register template' : 'Save'}</button></div>`
          : ''}
      </form>

      ${!isNew
        ? html`<section class="panel"><h2>Send a test</h2>
            <p class="hint">Renders this template in Stannp test mode with the data below and returns the PDF. Nothing is printed, posted or charged, and it never counts as the person's letter.</p>
            <textarea rows="12" aria-label="Sample data (JSON)" .value=${testData} @input=${(e: Event) => (testData = (e.target as HTMLTextAreaElement).value)}></textarea>
            <div class="actions-row">
              <button class="btn primary" ?disabled=${!editable || dirty} @click=${sendTest}>Send test</button>
              <button class="btn quiet" @click=${() => { testData = JSON.stringify(sampleData(d.fields), null, 2); app.rerender(); }}>Reset sample data</button>
              ${dirty ? html`<span class="muted">Save your changes first.</span>` : ''}
            </div>
            ${testError ? html`<div class="banner critical mt-4">${testError}</div>` : ''}
            ${testResult
              ? html`<div class="banner ${testResult.mailpiece.state === 'submitted' ? 'info' : 'warning'} mt-4">
                  ${testResult.mailpiece.pdf_url ? html`<b>Done.</b> <a href=${testResult.mailpiece.pdf_url} target="_blank" rel="noopener">Open the PDF</a>.` : html`<b>${testResult.outcome}</b>: ${testResult.mailpiece.last_error ?? ''}`}
                  ${testResult.ignored_fields.length ? html` Ignored (not template fields): ${testResult.ignored_fields.join(', ')}.` : ''}
                  <a href=${href('mail', testResult.mailpiece.id)}>Details</a></div>`
              : ''}
          </section>
          <section class="panel"><h2>Request for MoEngage</h2>
            <p class="hint">In a MoEngage connector campaign: <b>POST</b> to <code>${app.me.public_url}/stannp/send</code>, header <code>x-api-key</code> with the key,
              JSON body below. Replace each <code>{{…}}</code> with the matching attribute (type <b>@</b> in MoEngage to pick it).</p>
            <pre class="code">${moengageBody(d)}</pre>
            <button class="btn compact" @click=${() => copy(moengageBody(d), 'Request body copied')}>Copy body</button>
          </section>`
        : ''}`;
  }
}

// ---------------------------------------------------------------------------------------------
// Mail tab (#/connectors/stannp/mail and …/mail/{id})
// ---------------------------------------------------------------------------------------------
let mail: Mailpiece[] = [];
let cursor: string | null = null;
let piece: Mailpiece | null = null;
const filters = { state: '', template_id: '', ref_id: '', test: '' };
let templateNames: Record<string, string> = {};
let mailRest = '';

function mailQuery(extra: Record<string, string> = {}): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries({ ...filters, ...extra })) if (v) q.set(k, v);
  q.set('limit', '50');
  return `?${q}`;
}

async function loadMail(param: string): Promise<void> {
  mailRest = param;
  piece = null;
  if (param) {
    piece = (await get<{ mailpiece: Mailpiece }>(`${BASE}/mailpieces/${encodeURIComponent(param)}`)).mailpiece;
    return;
  }
  const [list, t] = await Promise.all([get<{ mailpieces: Mailpiece[]; cursor?: string | null }>(`${BASE}/mailpieces${mailQuery()}`), get<{ templates: Template[] }>(`${BASE}/templates`)]);
  mail = list.mailpieces;
  cursor = list.cursor ?? null;
  templateNames = Object.fromEntries(t.templates.map((x) => [x.stannp_template_id, x.name]));
}
const reloadMail = () => loadMail(mailRest);

async function more(): Promise<void> {
  if (!cursor) return;
  const r = await act(() => get<{ mailpieces: Mailpiece[]; cursor?: string | null }>(`${BASE}/mailpieces${mailQuery({ cursor: cursor! })}`), null);
  if (r) {
    mail = [...mail, ...r.mailpieces];
    cursor = r.cursor ?? null;
    app.rerender();
  }
}

const RECIPIENT_ORDER = ['firstname', 'lastname', 'address1', 'address2', 'city', 'state', 'zipcode', 'country'];
const recipientEntries = (r: Record<string, string>) =>
  Object.entries(r).sort(([a], [b]) => {
    const [ia, ib] = [RECIPIENT_ORDER.indexOf(a), RECIPIENT_ORDER.indexOf(b)];
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });
const who = (r: Record<string, string>) => [r.firstname, r.lastname].filter(Boolean).join(' ') || '—';
const sourceText = (m: Mailpiece) => (m.source.type === 'api_key' ? `API key “${m.source.name}”` : m.source.email);

function mailList(): TemplateResult {
  const apply = (e: Event) => {
    e.preventDefault();
    const v = Object.fromEntries(new FormData(e.target as HTMLFormElement)) as Record<string, string>;
    Object.assign(filters, { state: v.state ?? '', template_id: v.template_id ?? '', ref_id: (v.ref_id ?? '').trim(), test: v.test ?? '' });
    void act(() => reloadMail(), null);
  };
  return html`
    <p class="hint">Every letter and postcard sent through the Stannp connector, newest first. Held mail can be cancelled until it goes to Stannp.</p>
    <form class="filters" @submit=${apply}>
      <label>Status<select name="state">${[['', 'Any'], ['queued', 'Held'], ['submitted', 'Sent to Stannp'], ['failed', 'Failed'], ['cancelled', 'Cancelled']].map(
        ([v, l]) => html`<option value=${v!} ?selected=${filters.state === v}>${l}</option>`,
      )}</select></label>
      <label>Template<select name="template_id"><option value="">Any</option>${Object.entries(templateNames).map(
        ([id, n]) => html`<option value=${id} ?selected=${filters.template_id === id}>${n}</option>`,
      )}</select></label>
      <label>Mode<select name="test">${[['', 'Live and test'], ['false', 'Live only'], ['true', 'Test only']].map(([v, l]) => html`<option value=${v!} ?selected=${filters.test === v}>${l}</option>`)}</select></label>
      <label>ref_id<input name="ref_id" type="search" placeholder="CRM client ID" .value=${filters.ref_id}></label>
      <button class="btn">Show</button>
    </form>
    <div class="table-wrap"><table class="clickable">
      <thead><tr><th>Created</th><th>Recipient</th><th>ref_id</th><th>Template</th><th>Status</th><th>Sends</th></tr></thead>
      <tbody>${mail.length
        ? mail.map(
            (m) => html`<tr @click=${() => (location.hash = href('mail', m.id))}>
              <td class="nowrap">${when(m.created_at)}</td>
              <td>${who(m.recipient)}<br><span class="muted">${m.recipient.city ?? ''} ${m.recipient.state ?? ''}</span></td>
              <td><code class="id">${m.ref_id}</code></td>
              <td>${m.template_name}<br><code class="id">${m.stannp_template_id}</code></td>
              <td>${mailPill(m.status)} ${m.test ? pill('Test', 'info') : ''}</td>
              <td class="nowrap">${m.state === 'queued' ? `at ${when(m.next_attempt_at ?? m.send_at)}` : when(m.submitted_at)}</td>
            </tr>`,
          )
        : emptyRow(6, 'No mail matches.')}</tbody>
    </table></div>
    ${cursor ? html`<div class="actions-row"><button class="btn" @click=${more}>Load more</button></div>` : ''}`;
}

function mailDetail(m: Mailpiece): TemplateResult {
  const admin = isAdmin();
  const cancel = async () => {
    const ok = await confirmDialog({
      title: 'Cancel this mailpiece?',
      body: m.state === 'queued' ? 'It is still held here, so it will simply never be sent.' : 'Stannp can cancel it only until printing starts.',
      confirmLabel: 'Cancel mailpiece',
      tone: 'danger',
    });
    if (ok) await act(() => api('POST', `${BASE}/mailpieces/${m.id}/cancel`), 'Cancelled', reloadMail);
  };
  const resend = async (immediate: boolean) => {
    const ok = await confirmDialog({
      title: immediate ? 'Resend now?' : 'Resend with the usual hold?',
      body: `A new ${m.type} with the same data goes to ${who(m.recipient)}${m.test ? ' (test mode)' : ''}${immediate ? ' straight away' : ' after the template’s hold'}.`,
      confirmLabel: 'Resend',
    });
    if (!ok) return;
    const r = await act(() => api<{ mailpiece: Mailpiece }>('POST', `${BASE}/mailpieces/${m.id}/resend`, { immediate }), 'Resend created');
    if (r) location.hash = href('mail', r.mailpiece.id);
  };
  const refresh = () => act(() => api('POST', `${BASE}/mailpieces/${m.id}/refresh`), 'Status refreshed', reloadMail);
  const canCancel = m.state === 'queued' || (m.state === 'submitted' && m.stannp_status !== 'cancelled');
  return html`
    <p class="crumb"><a href=${href('mail')}>‹ Mail</a></p>
    <div class="row-between page-head"><h2>${who(m.recipient)}</h2><span>${mailPill(m.status)} ${m.test ? pill('Test mode', 'info') : ''}</span></div>
    ${m.state === 'failed' ? html`<div class="banner critical"><b>Not sent.</b> ${m.last_error ?? ''} Fix the data at the source and send again, or resend from here.</div>` : ''}
    ${m.state === 'queued' && m.attempts > 0 ? html`<div class="banner warning">Attempt ${m.attempts} failed: ${m.last_error}. Next try ${when(m.next_attempt_at)}.</div>` : ''}
    ${admin
      ? html`<div class="actions-row mb-5">
          ${canCancel ? html`<button class="btn destructive outline" @click=${cancel}>Cancel</button>` : ''}
          <button class="btn" @click=${() => resend(false)}>Resend</button>
          <button class="btn quiet" @click=${() => resend(true)}>Resend now</button>
          ${m.stannp_id ? html`<button class="btn quiet" @click=${refresh}>Refresh status</button>` : ''}
          ${m.pdf_url ? html`<a class="btn quiet" href=${m.pdf_url} target="_blank" rel="noopener">Open PDF</a>` : ''}
        </div>`
      : m.pdf_url ? html`<p><a href=${m.pdf_url} target="_blank" rel="noopener">Open PDF</a></p>` : ''}
    <div class="grid two">
      <div class="card"><h2>Mailpiece</h2><dl class="kv">
        <dt>Template</dt><dd>${m.template_name} <code class="id">${m.stannp_template_id}</code></dd>
        <dt>ref_id</dt><dd><code>${m.ref_id}</code></dd>
        <dt>Sent by</dt><dd>${sourceText(m)}</dd>
        <dt>Created</dt><dd>${when(m.created_at)}</dd>
        <dt>${m.state === 'queued' ? 'Goes out' : 'Due'}</dt><dd>${when(m.next_attempt_at ?? m.send_at)}</dd>
        <dt>Sent to Stannp</dt><dd>${when(m.submitted_at)}</dd>
        <dt>Stannp ID</dt><dd>${m.stannp_id ?? '—'}</dd>
        <dt>Cost</dt><dd>${m.cost ? `$${m.cost}` : '—'}</dd>
        <dt>Attempts</dt><dd>${m.attempts}</dd>
        ${m.resend_of ? html`<dt>Resend of</dt><dd><a href=${href('mail', m.resend_of)}>earlier mailpiece</a></dd>` : ''}
        ${m.cancelled_at ? html`<dt>Cancelled</dt><dd>${when(m.cancelled_at)} by ${m.cancelled_by}</dd>` : ''}
      </dl></div>
      <div class="card"><h2>Recipient data</h2><dl class="kv">
        ${recipientEntries(m.recipient).map(([k, v]) => html`<dt>${k}</dt><dd>${v}</dd>`)}
      </dl></div>
    </div>
    <section class="panel"><h2>History</h2><div class="card"><ul class="history">
      ${[...m.history].reverse().map((h) => html`<li><span class="muted">${when(h.at)}</span><span><b>${h.event.replace(/^status:/, 'Status: ').replace(/_/g, ' ')}</b>${h.by ? html` · <span class="muted">${h.by}</span>` : ''}${h.detail ? html`<br>${h.detail}` : ''}</span></li>`)}
    </ul></div></section>`;
}

// ---------------------------------------------------------------------------------------------
// Registration: the tabs on Connectors › Stannp
// ---------------------------------------------------------------------------------------------
let templatesRest = '';

export const stannpUi: ConnectorUi = {
  loadSettings: loadAccount,
  settings: accountPanels,
  tabs: [
    {
      id: 'templates',
      label: 'Templates',
      async load(rest) {
        templatesRest = rest;
        await (rest ? loadTemplate(rest) : loadTemplateList());
      },
      view: () => (templatesRest ? templateEditor() : templateList()),
    },
    { id: 'mail', label: 'Mail', load: loadMail, view: () => (piece ? mailDetail(piece) : mailList()) },
  ],
};
